<template>
    <div class="fedex-registration-wizard">
        <b-overlay :show="loading" rounded="sm">
            <!-- Registered: show audit trail -->
            <div v-if="isRegistered && status" class="registered-summary">
                <div class="mb-2">
                    <i class="fa fa-check-circle text-success mr-1"></i>
                    <strong>FedEx customer registration complete</strong>
                </div>
                <div class="text-muted small">
                    Registered {{ formatDate(status.registeredAt) }}
                    via <code>{{ status.factor2Method }}</code>
                    &mdash; account <code>{{ status.accountNumber }}</code>
                </div>
                <div class="mt-2 text-muted small">
                    Child credentials are stored and encrypted. FedEx Direct is now usable
                    for this store.
                </div>
                <div class="mt-3">
                    <b-button size="sm" variant="outline-danger" :disabled="submitting" @click="offboard">
                        <i class="fa fa-power-off mr-1"></i> Turn off FedEx Direct
                    </b-button>
                </div>
                <b-alert :show="!!errorMessage"
                         variant="danger"
                         class="mt-3 mb-0"
                         dismissible
                         @dismissed="errorMessage = null">
                    {{ errorMessage }}
                </b-alert>
            </div>

            <!-- Pending support escalation: show notes + "start over" option -->
            <div v-else-if="isPendingSupport && status" class="pending-support">
                <b-alert show variant="warning" class="mb-2">
                    <strong>Waiting on FedEx technical support.</strong>
                    <div v-if="status.registrationNotes" class="mt-1">
                        {{ status.registrationNotes }}
                    </div>
                </b-alert>
                <b-button size="sm" variant="outline-secondary" @click="restart">
                    <i class="fa fa-refresh mr-1"></i> Start over
                </b-button>
            </div>

            <!-- MFA locked (24h per FedEx). Per AGD: customer can try another method or call FedEx Customer Service. -->
            <div v-else-if="isMfaLocked && status" class="mfa-locked">
                <b-alert show variant="danger" class="mb-2">
                    <strong>MFA temporarily locked.</strong>
                    <div v-if="status.registrationNotes" class="mt-1">
                        {{ status.registrationNotes }}
                    </div>
                    <div class="mt-2">
                        We are unable to process this request. Please try again later or call FedEx
                        Customer Service and ask for technical support.
                    </div>
                </b-alert>
                <b-button size="sm" variant="outline-secondary" @click="restart">
                    <i class="fa fa-refresh mr-1"></i> Try a different method
                </b-button>
            </div>

            <!-- Active wizard -->
            <div v-else-if="status">
                <b-alert v-if="isOffboarded" show variant="info" class="mb-3">
                    FedEx Direct is turned off for this store &mdash; ShipEngine is handling FedEx
                    shipments. Complete the steps below to turn it back on.
                </b-alert>

                <!-- Step summary ribbon -->
                <div class="step-ribbon mb-3">
                    <span class="step-pill" :class="{ 'active': activeStep === 'factor1', 'done': factor1Done }">
                        <i v-if="factor1Done" class="fa fa-check mr-1"></i>
                        1. Verify Account
                    </span>
                    <i class="fa fa-angle-right step-arrow"></i>
                    <span class="step-pill" :class="{ 'active': activeStep === 'factor2', 'done': factor2Done }">
                        <i v-if="factor2Done" class="fa fa-check mr-1"></i>
                        2. Multi-Factor
                    </span>
                    <i class="fa fa-angle-right step-arrow"></i>
                    <span class="step-pill" :class="{ 'active': activeStep === 'done', 'done': isRegistered }">
                        3. Done
                    </span>
                </div>

                <!-- Factor 1: Account + Address -->
                <div v-if="activeStep === 'factor1'" class="wizard-step">
                    <p class="text-muted mb-3">
                        Enter the master FedEx account number for this pharmacy along with the
                        account holder address on file with FedEx. FedEx will verify this
                        combination before proceeding to the second factor.
                    </p>

                    <b-form-group label="FedEx Account Number" label-for="fedex-account">
                        <b-form-input id="fedex-account"
                                      v-model="factor1.accountNumber"
                                      :disabled="submitting"
                                      placeholder="e.g. 740561073" />
                    </b-form-group>

                    <b-form-group label="Customer Name (as registered with FedEx)"
                                  description="Must match the customer/company name FedEx has on file. FedEx enforces uniqueness per account.">
                        <b-form-input v-model="factor1.customerName"
                                      :disabled="submitting"
                                      maxlength="50"
                                      placeholder="e.g. Scriptly Pharmacy" />
                    </b-form-group>

                    <b-form-group label="Street Address">
                        <b-form-input v-model="factor1.streetLines"
                                      :disabled="submitting"
                                      placeholder="Street line 1" />
                    </b-form-group>

                    <b-row>
                        <b-col md="6">
                            <b-form-group label="City">
                                <b-form-input v-model="factor1.city" :disabled="submitting" />
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group label="State / Province">
                                <b-form-input v-model="factor1.stateOrProvinceCode"
                                              :disabled="submitting"
                                              maxlength="5"
                                              placeholder="TX" />
                            </b-form-group>
                        </b-col>
                        <b-col md="3">
                            <b-form-group label="Postal Code">
                                <b-form-input v-model="factor1.postalCode" :disabled="submitting" />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Country Code">
                                <b-form-input v-model="factor1.countryCode"
                                              :disabled="submitting"
                                              maxlength="2" />
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Address type"
                                          description="Pharmacies → Commercial. Some FedEx sandbox test fixtures use Residential.">
                                <b-form-checkbox v-model="factor1.residential"
                                                 :disabled="submitting"
                                                 switch>
                                    {{ factor1.residential ? 'Residential' : 'Commercial' }}
                                </b-form-checkbox>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <div class="mt-3">
                        <b-button variant="primary"
                                  :disabled="!canSubmitFactor1 || submitting"
                                  @click="submitFactor1">
                            <b-spinner v-if="submitting" small class="mr-1" />
                            Verify Account
                        </b-button>
                    </div>
                </div>

                <!-- Factor 2 method picker -->
                <div v-else-if="activeStep === 'factor2' && !factor2MethodChosen" class="wizard-step">
                    <p class="text-muted mb-3">
                        Choose how FedEx will deliver the second factor. PIN-based methods send
                        a one-time code; Invoice validation uses a recent FedEx invoice number
                        for your account.
                    </p>

                    <div class="factor2-picker">
                        <b-button variant="outline-primary"
                                  class="factor2-option"
                                  :disabled="submitting"
                                  @click="chooseMethod('Phone')">
                            <i class="fa fa-phone mr-2"></i> Phone PIN
                        </b-button>
                        <b-button variant="outline-primary"
                                  class="factor2-option"
                                  :disabled="submitting"
                                  @click="chooseMethod('Email')">
                            <i class="fa fa-envelope mr-2"></i> Email PIN
                        </b-button>
                        <b-button variant="outline-primary"
                                  class="factor2-option"
                                  :disabled="submitting"
                                  @click="chooseMethod('Sms')">
                            <i class="fa fa-mobile mr-2"></i> SMS PIN
                        </b-button>
                        <b-button variant="outline-primary"
                                  class="factor2-option"
                                  :disabled="submitting"
                                  @click="chooseMethod('Invoice')">
                            <i class="fa fa-file-text-o mr-2"></i> Invoice Number
                        </b-button>
                    </div>
                </div>

                <!-- Factor 2: PIN entry (after request-pin succeeded) -->
                <div v-else-if="activeStep === 'factor2' && chosenMethod && chosenMethod !== 'Invoice' && chosenMethod !== 'SupportEscalation' && pinRequested" class="wizard-step">
                    <p class="text-muted mb-2">
                        Enter the 6-digit PIN delivered to the contact on file with this FedEx
                        account via <strong>{{ chosenMethodLabel }}</strong>. The destination
                        (email address / phone number) is whatever the pharmacy registered with
                        FedEx when opening their account &mdash; we do not see or control it.
                    </p>

                    <b-form-group label="6-digit PIN">
                        <b-form-input v-model="pin"
                                      :disabled="submitting"
                                      inputmode="numeric"
                                      maxlength="6"
                                      pattern="[0-9]{6}"
                                      autocomplete="one-time-code"
                                      :state="pin.length === 0 ? null : isPinValid" />
                        <b-form-invalid-feedback v-if="pin && !isPinValid">
                            PIN must be exactly 6 digits.
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <div class="d-flex gap-2">
                        <b-button variant="primary"
                                  :disabled="!isPinValid || submitting"
                                  @click="submitPin">
                            <b-spinner v-if="submitting" small class="mr-1" />
                            Submit PIN
                        </b-button>
                        <b-button variant="secondary"
                                  class="ml-2 choose-method-btn"
                                  :disabled="submitting"
                                  @click="resetFactor2">
                            Choose a different method
                        </b-button>
                    </div>
                </div>

                <!-- Factor 2: Invoice entry — AGD requires all four fields and invoice &lt; 90 days old -->
                <div v-else-if="activeStep === 'factor2' && chosenMethod === 'Invoice'" class="wizard-step">
                    <p class="text-muted mb-2">
                        Enter the details from a recent FedEx invoice for this account. The
                        invoice must be less than 90 days old and tied to a 9-digit FedEx
                        account number (not TNT or other acquired companies).
                    </p>

                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Invoice Number">
                                <b-form-input v-model="invoiceNumber" :disabled="submitting" maxlength="50" />
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Invoice Date">
                                <b-form-input v-model="invoiceDate"
                                              :disabled="submitting"
                                              type="date"
                                              :max="todayIso"
                                              :min="ninetyDaysAgoIso" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Invoice Amount">
                                <b-form-input v-model.number="invoiceAmount"
                                              :disabled="submitting"
                                              type="number"
                                              step="0.01"
                                              min="0" />
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Currency">
                                <b-form-select v-model="invoiceCurrency"
                                               :disabled="submitting"
                                               :options="currencyOptions" />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <div class="d-flex gap-2">
                        <b-button variant="primary"
                                  :disabled="!canSubmitInvoice || submitting"
                                  @click="submitInvoice">
                            <b-spinner v-if="submitting" small class="mr-1" />
                            Validate Invoice
                        </b-button>
                        <b-button variant="secondary"
                                  class="ml-2 choose-method-btn"
                                  :disabled="submitting"
                                  @click="resetFactor2">
                            Choose a different method
                        </b-button>
                    </div>
                </div>

                <!-- Support escalation form -->
                <div v-else-if="escalating" class="wizard-step">
                    <p class="text-muted mb-2">
                        Describe what happened so your operations team knows what to pass along
                        to FedEx technical support.
                    </p>

                    <b-form-group label="Notes">
                        <b-form-textarea v-model="escalateNotes"
                                         :disabled="submitting"
                                         rows="3"
                                         max-rows="6" />
                    </b-form-group>

                    <div class="d-flex gap-2">
                        <b-button variant="warning"
                                  :disabled="submitting"
                                  @click="submitEscalate">
                            <b-spinner v-if="submitting" small class="mr-1" />
                            Escalate
                        </b-button>
                        <b-button variant="outline-secondary"
                                  class="ml-2"
                                  :disabled="submitting"
                                  @click="escalating = false">
                            Cancel
                        </b-button>
                    </div>
                </div>

                <b-alert v-if="errorMessage"
                         show
                         variant="danger"
                         class="mt-3"
                         dismissible
                         @dismissed="errorMessage = null">
                    {{ errorMessage }}
                </b-alert>

                <!-- Post-failure-only support escalation prompt (AGD: show only after MFA fails,
                     and use the verbatim error message). Kept deliberately subtle so users don't
                     discover it as a shortcut. -->
                <div v-if="showSupportPrompt" class="support-fallback mt-3">
                    <b-alert show variant="warning">
                        We are unable to process this request. Please try again later or call FedEx
                        Customer Service and ask for technical support.
                    </b-alert>
                    <div class="d-flex">
                        <b-button variant="outline-warning" size="sm" @click="beginEscalate">
                            <i class="fa fa-life-ring mr-1"></i>
                            I've contacted FedEx &mdash; mark this store pending support
                        </b-button>
                    </div>
                </div>
            </div>
        </b-overlay>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { FedExRegistrationService } from "@/services/FedExRegistrationService";
import {
    FedExRegistrationStatusResponse,
    FedExRegistrationStatus,
    FedExPinDeliveryMethod,
} from "@/models/FedEx/FedExRegistration";

type Factor2Choice = FedExPinDeliveryMethod | "Invoice" | "SupportEscalation" | null;
type ActiveStep = "factor1" | "factor2" | "done";

@Component({
    name: "FedExRegistrationWizard"
})
export default class FedExRegistrationWizard extends Vue {
    @Prop({ required: true }) protected storeId!: number;
    @Prop({ default: true }) protected eulaAccepted!: boolean;

    protected status: FedExRegistrationStatusResponse | null = null;
    protected loading: boolean = false;
    protected submitting: boolean = false;
    protected errorMessage: string | null = null;

    protected factor1 = {
        accountNumber: "",
        customerName: "",
        residential: false,
        streetLines: "",
        city: "",
        stateOrProvinceCode: "",
        postalCode: "",
        countryCode: "US",
    };

    protected chosenMethod: Factor2Choice = null;
    protected pinRequested: boolean = false;
    protected pin: string = "";
    protected invoiceNumber: string = "";
    protected invoiceDate: string = "";
    protected invoiceAmount: number | null = null;
    protected invoiceCurrency: string = "USD";
    protected escalating: boolean = false;
    protected escalateNotes: string = "";

    /** Number of Factor 2 failures in this session — drives when to surface the support prompt. */
    protected factor2FailureCount: number = 0;

    protected readonly currencyOptions = [
        { value: "USD", text: "USD — US Dollar" },
        { value: "CAD", text: "CAD — Canadian Dollar" },
        { value: "EUR", text: "EUR — Euro" },
        { value: "GBP", text: "GBP — British Pound" },
        { value: "MXN", text: "MXN — Mexican Peso" },
    ];

    async created() {
        await this.load();
    }

    @Watch("storeId")
    async onStoreChange() {
        this.resetState();
        await this.load();
    }

    get isRegistered(): boolean {
        return this.status?.registrationStatus === FedExRegistrationStatus.Registered;
    }

    get isPendingSupport(): boolean {
        return this.status?.registrationStatus === FedExRegistrationStatus.PendingSupport;
    }

    get isMfaLocked(): boolean {
        return this.status?.registrationStatus === FedExRegistrationStatus.MfaLocked;
    }

    get isOffboarded(): boolean {
        return this.status?.registrationStatus === FedExRegistrationStatus.Offboarded;
    }

    get chosenMethodLabel(): string {
        switch (this.chosenMethod) {
            case "Phone": return "phone call";
            case "Email": return "email";
            case "Sms":   return "SMS";
            default:      return String(this.chosenMethod ?? "");
        }
    }

    get isPinValid(): boolean {
        return /^\d{6}$/.test(this.pin);
    }

    get canSubmitInvoice(): boolean {
        return !!this.invoiceNumber
            && !!this.invoiceDate
            && this.invoiceAmount !== null
            && (this.invoiceAmount ?? 0) > 0
            && !!this.invoiceCurrency;
    }

    get showSupportPrompt(): boolean {
        // AGD: do not show as an upfront choice; surface only after at least one Factor 2 attempt fails.
        return this.factor2FailureCount > 0 && !this.isRegistered && !this.isPendingSupport && !this.isMfaLocked;
    }

    get todayIso(): string {
        return new Date().toISOString().slice(0, 10);
    }

    get ninetyDaysAgoIso(): string {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        return d.toISOString().slice(0, 10);
    }

    get factor1Done(): boolean {
        if (!this.status) return false;
        return this.status.registrationStatus >= FedExRegistrationStatus.Factor1Complete
            && this.status.registrationStatus !== FedExRegistrationStatus.Pending
            && this.status.registrationStatus !== FedExRegistrationStatus.Offboarded;
    }

    get factor2Done(): boolean {
        return this.isRegistered;
    }

    get activeStep(): ActiveStep {
        if (this.isRegistered) return "done";
        if (this.factor1Done) return "factor2";
        return "factor1";
    }

    get factor2MethodChosen(): boolean {
        return this.chosenMethod !== null && !this.escalating;
    }

    get canSubmitFactor1(): boolean {
        return !!this.factor1.accountNumber
            && !!this.factor1.customerName
            && !!this.factor1.streetLines
            && !!this.factor1.city
            && !!this.factor1.stateOrProvinceCode
            && !!this.factor1.postalCode;
    }

    async load() {
        if (!this.storeId) {
            this.status = null;
            return;
        }
        this.loading = true;
        try {
            this.status = await FedExRegistrationService.getStatus(this.storeId);
            if (this.status?.accountNumber && !this.factor1.accountNumber) {
                this.factor1.accountNumber = this.status.accountNumber;
            }
        } catch (err: any) {
            this.errorMessage = this.resolveError(err, "Failed to load FedEx registration status.");
        } finally {
            this.loading = false;
        }
    }

    async submitFactor1() {
        if (!this.canSubmitFactor1) return;
        this.submitting = true;
        this.errorMessage = null;
        try {
            this.status = await FedExRegistrationService.completeFactor1(this.storeId, {
                accountNumber: this.factor1.accountNumber,
                customerName: this.factor1.customerName,
                residential: this.factor1.residential,
                streetLines: this.factor1.streetLines || null,
                city: this.factor1.city || null,
                stateOrProvinceCode: this.factor1.stateOrProvinceCode || null,
                postalCode: this.factor1.postalCode || null,
                countryCode: this.factor1.countryCode || "US",
            });
            this.$emit("status-changed");
        } catch (err: any) {
            this.errorMessage = this.resolveError(err, "FedEx declined the account verification.");
        } finally {
            this.submitting = false;
        }
    }

    async chooseMethod(method: FedExPinDeliveryMethod | "Invoice") {
        this.errorMessage = null;

        if (method === "Invoice") {
            this.chosenMethod = "Invoice";
            this.pinRequested = false;
            return;
        }

        this.submitting = true;
        try {
            this.status = await FedExRegistrationService.requestPin(this.storeId, {
                deliveryMethod: method,
            });
            this.chosenMethod = method;
            this.pinRequested = true;
            this.$emit("status-changed");
        } catch (err: any) {
            this.errorMessage = this.resolveError(err, "FedEx could not send a PIN via that method.");
        } finally {
            this.submitting = false;
        }
    }

    async submitPin() {
        if (!this.isPinValid) return;
        this.submitting = true;
        this.errorMessage = null;
        try {
            this.status = await FedExRegistrationService.submitPin(this.storeId, { pin: this.pin });
            this.pin = "";
            this.$emit("status-changed");
        } catch (err: any) {
            this.factor2FailureCount += 1;
            this.errorMessage = this.resolveError(err, "FedEx rejected the PIN.");
        } finally {
            this.submitting = false;
        }
    }

    async submitInvoice() {
        if (!this.canSubmitInvoice) return;
        this.submitting = true;
        this.errorMessage = null;
        try {
            this.status = await FedExRegistrationService.submitInvoice(this.storeId, {
                invoiceNumber: this.invoiceNumber,
                invoiceDate: this.invoiceDate,
                invoiceAmount: this.invoiceAmount ?? 0,
                currency: this.invoiceCurrency,
            });
            this.invoiceNumber = "";
            this.invoiceDate = "";
            this.invoiceAmount = null;
            this.$emit("status-changed");
        } catch (err: any) {
            this.factor2FailureCount += 1;
            this.errorMessage = this.resolveError(err, "FedEx rejected the invoice details.");
        } finally {
            this.submitting = false;
        }
    }

    beginEscalate() {
        this.escalating = true;
        this.chosenMethod = "SupportEscalation";
    }

    async submitEscalate() {
        this.submitting = true;
        this.errorMessage = null;
        try {
            this.status = await FedExRegistrationService.escalateToSupport(this.storeId, {
                notes: this.escalateNotes || null,
            });
            this.escalating = false;
            this.$emit("status-changed");
        } catch (err: any) {
            this.errorMessage = this.resolveError(err, "Failed to escalate to support.");
        } finally {
            this.submitting = false;
        }
    }

    async offboard() {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            "Turn off FedEx Direct for this store? ShipEngine will handle FedEx shipments again. Your EULA acceptance is kept, and you can re-register anytime.",
            {
                title: "Turn off FedEx Direct",
                okTitle: "Turn off",
                okVariant: "danger",
                cancelTitle: "Keep it on",
                centered: true,
            },
        );
        if (!confirmed) return;

        this.submitting = true;
        this.errorMessage = null;
        try {
            await FedExRegistrationService.offboard(this.storeId);
            this.$emit("status-changed");
            this.restart();
        } catch (err: any) {
            this.errorMessage = this.resolveError(err, "Failed to turn off FedEx Direct.");
        } finally {
            this.submitting = false;
        }
    }

    resetFactor2() {
        this.chosenMethod = null;
        this.pinRequested = false;
        this.pin = "";
        this.invoiceNumber = "";
        this.errorMessage = null;
    }

    restart() {
        this.resetState();
        this.load();
    }

    private resetState() {
        this.chosenMethod = null;
        this.pinRequested = false;
        this.pin = "";
        this.invoiceNumber = "";
        this.invoiceDate = "";
        this.invoiceAmount = null;
        this.invoiceCurrency = "USD";
        this.escalating = false;
        this.escalateNotes = "";
        this.errorMessage = null;
        this.factor2FailureCount = 0;
    }

    formatDate(iso: string | null): string {
        if (!iso) return "";
        return new Date(iso).toLocaleString();
    }

    private resolveError(err: any, fallback: string): string {
        const data = err?.response?.data;
        if (typeof data === "string" && data.length > 0) return data;
        if (data?.title) return data.title;
        return fallback;
    }
}
</script>

<style scoped>
.step-ribbon {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.step-pill {
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    background-color: var(--bg-subtle, #e9ecef);
    color: var(--text-muted, #6c757d);
    font-size: 0.85rem;
    font-weight: 500;
}

.step-pill.active {
    background-color: var(--primary, #007bff);
    color: #fff;
}

.step-pill.done {
    background-color: var(--success, #28a745);
    color: #fff;
}

.step-arrow {
    color: var(--text-muted, #6c757d);
}

.wizard-step {
    padding-top: 0.5rem;
}

.factor2-picker {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.5rem;
}

.factor2-option {
    justify-content: flex-start;
    text-align: left;
}

/* Always-visible "Choose a different method" — solid grey instead of outline-only,
   stays readable without hover. */
.choose-method-btn,
.choose-method-btn:focus {
    background-color: #6c757d !important;
    border-color: #6c757d !important;
    color: #fff !important;
    opacity: 1 !important;
}
.choose-method-btn:hover:not(:disabled) {
    background-color: #5a6268 !important;
    border-color: #545b62 !important;
}
.choose-method-btn:disabled {
    background-color: #adb5bd !important;
    border-color: #adb5bd !important;
    color: #fff !important;
}
</style>
